import { DatePipe, NgClass } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService, PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import { InputNumber } from 'primeng/inputnumber';
import { Message } from 'primeng/message';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { TransactionCreate } from '../../../content/dtos/transaction/transaction-create';
import { DialogTableDataRow } from '../../../content/interfaces/dialog-table-data-row';
import { TransactionData } from '../../../content/interfaces/transaction/transaction-data';
import { TransactionCreditToTableDataMapper } from '../../../content/mapper/transaction/transaction-credit-to-table-data-mapper.service';
import { TransactionDataToTransactionCreateMapperService } from '../../../content/mapper/transaction/transaction-data-to-transaction-create-mapper.service';
import { TransactionDetailsToTableDataMapper } from '../../../content/mapper/transaction/transaction-details-to-table-data-mapper.service';
import { TransactionHistoryToTableDataMapper } from '../../../content/mapper/transaction/transaction-history-to-table-data-mapper.service';
import { TransactionRefundToTableDataMapper } from '../../../content/mapper/transaction/transaction-refund-to-table-data-mapper.service';
import { TransactionSenderToTableDataMapper } from '../../../content/mapper/transaction/transaction-sender-to-table-data-mapper.service';
import { TransactionToTransactionViewDataMapperService } from '../../../content/mapper/transaction/transaction-to-transaction-view-data-mapper.service';
import { CurrencyToSymbolPipe } from '../../../content/pipes/currency-to-symbol.pipe';
import { TransactionHandlingService } from '../../../content/services/transaction-handling.service';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { I18nService } from '../../../core/services/i18n.service';
import { LayoutService } from '../../../layout/services/layout.service';
import { SpsPaymentMethodIconTextComponent } from '../sps-payment-method-icon-text/sps-payment-method-icon-text.component';

@Component({
  selector: 'sps-transaction-details-dialog',
  imports: [
    PrimeTemplate,
    TableModule,
    Tag,
    NgClass,
    SpsPaymentMethodIconTextComponent,
    DatePipe,
    CurrencyToSymbolPipe,
    InputNumber,
    FormsModule,
    Button,
    Message
  ],
  templateUrl: './sps-transaction-details-dialog.component.html'
})
export class SpsTransactionDetailsDialogComponent implements OnInit {

  @Input() transactionId: string = null;
  @Input() titleColumnWidth: string = 'auto';
  @Input() valueColumnWidth: string = 'auto';
  @Input() transactionViewMode: 'TENANT' | 'ADMIN';
  @Input() showTransactionError = signal<boolean>(false);
  @Input() transactionErrorMessage = signal<String>('');
  @Output() transactionIdChanged = new EventEmitter<string>();
  @Output() processCapture = new EventEmitter<TransactionData>();
  @Output() processVoid = new EventEmitter<TransactionData>();
  @Output() refundSuccessful = new EventEmitter();
  @Output() creditSuccessful = new EventEmitter();

  transactionDetailData: TransactionData = null;

  /**
   * Table-Data for data-sections in Transaction-Details Dialog
   */
  tableDataDetails: DialogTableDataRow[] = [];
  tableDataSender: DialogTableDataRow[] = null;
  tableDataRefund: DialogTableDataRow[] = null;
  tableDataCredit: DialogTableDataRow[] = null;  // Gutschrift (Chargeback)
  tableDataHistory: DialogTableDataRow[] = null;

  /**
   * i18n and services
   */
  i18n = inject(I18nService);
  private layoutService = inject(LayoutService);
  private transactionHandlingService = inject(TransactionHandlingService);
  private transactionDataMapperService = inject(TransactionToTransactionViewDataMapperService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  /**
   * Data-Mapper
   */
  transactionDetailsMapper = inject(TransactionDetailsToTableDataMapper);
  transactionSenderMapper = inject(TransactionSenderToTableDataMapper);
  transactionRefundMapper = inject(TransactionRefundToTableDataMapper);
  transactionCreditMapper = inject(TransactionCreditToTableDataMapper);
  transactionHistoryMapper = inject(TransactionHistoryToTableDataMapper);
  transactionCreateMapper = inject(TransactionDataToTransactionCreateMapperService);

  /**
   * Refund-Flag
   */
  refundEnabled: boolean = false;

  /**
   * Chargeback-Flag
   */
  creditEnabled: boolean = false;

  /**
   * Type of Transaction
   */
  transactionType: 'REFUND' | 'CREDIT';

  ngOnInit(): void {
    if (this.transactionId) {
      this.getTransactionDetails();
    } else {
      this.mapTableData();
    }
  }

  /**
   * get details data from the api
   */
  getTransactionDetails(): void {
    this.transactionHandlingService.findById(SERVICE_ENDPOINTS.GET_TRANSACTION, this.transactionId).subscribe({
      next: result => {
        if (result.successful) {
          this.transactionDetailData = this.transactionDataMapperService.map(result.dto);
          this.transactionIdChanged.next(this.transactionDetailData.id)
          this.mapTableData();
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: result.message,
            detail: result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      },
      error: err => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
      }
    });
  }

  /**
   * Mapping the tables for the different data sections
   * @private
   */
  private mapTableData() {
    this.tableDataHistory = null;
    this.tableDataDetails = this.transactionDetailsMapper.map(this.transactionDetailData);
    this.tableDataSender = this.transactionSenderMapper.map(this.transactionDetailData);
    this.tableDataRefund = this.transactionRefundMapper.map(this.transactionDetailData);
    this.tableDataCredit = this.transactionCreditMapper.map(this.transactionDetailData);
    this.transactionHistoryMapper.reset();  // recursion reset is needed
    this.tableDataHistory = this.transactionHistoryMapper.map(this.transactionDetailData.history, this.transactionDetailData.id);
  }
  /**
   * Check the refund amount and enable/disable refund-button
   */
  checkRefundAmount() {
    this.refundEnabled = this.transactionDetailData?.refundAmount && (this.transactionDetailData.refundAmount > 0) && (Math.floor((this.transactionDetailData.refundAmount * 100)) <= this.transactionDetailData.refundAmountMax);
  }

  /**
   * currently only check for max. amount of payment
   * @protected
   */
  protected checkCreditAmount() {
    this.creditEnabled = this.transactionDetailData?.creditAmount && (this.transactionDetailData.creditAmount > 0);
  }

  /**
   * Initialize the refund for the current transaction
   * @protected
   */
  protected initRefund() {
    this.transactionType = 'REFUND';
    this.showTransactionError.set(false);
    this.transactionErrorMessage.set('');
    const formattedAmount = new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: this.transactionDetailData.currency,
    }).format(this.transactionDetailData.refundAmount);

    this.confirmationService.confirm({
      position: 'center',
      message: (this.i18n.i18nLng()['CONFIRMREFUND']).replace("#1", formattedAmount).replace("#2", this.transactionDetailData.id),
      header: this.i18n.i18nLng()['CONFIRMATION'],
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-question-circle',
      acceptButtonProps: {
        label: this.i18n.i18nLng()['REFUNDBUTTON']
      },
      rejectButtonProps: {
        label: this.i18n.i18nLng()['CANCEL'],
        outlined: true,
        severity: 'secondary'
      },
      accept: () => {
        this.layoutService.progressDialogTitle = this.i18n.i18nLng()['PROCESSINGTRANSACTION'];
        this.layoutService.showProgressSpinner = true;
        this.layoutService.showProgressDialog = true;

        const transactionCreate: TransactionCreate = this.transactionCreateMapper.map(this.transactionDetailData, 'REFUND');

        this.transactionHandlingService.createChildTransaction(SERVICE_ENDPOINTS.CREATE_TRANSACTION, 'refund', this.transactionDetailData.id, transactionCreate).subscribe({
          next: result => {
            this.layoutService.showProgressDialog = false;
            if (result.successful) {
              this.refundSuccessful.emit();
            } else {
              this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + result.message);
              this.showTransactionError.set(true);
            }
          },
          error: err => {
            this.layoutService.showProgressDialog = false;
            this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + err.message);
            this.showTransactionError.set(true);
          }
        })
      }
    });
  }

  /**
   * Initialize the chargeback for the current transaction   * @protected
   */
  protected initChargeback() {
    this.transactionType = 'CREDIT';
    this.showTransactionError.set(false);
    this.transactionErrorMessage.set('');
    const formattedAmount = new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: this.transactionDetailData.currency,
    }).format(this.transactionDetailData.creditAmount);
    this.confirmationService.confirm({
      position: 'center',
      message: (this.i18n.i18nLng()['CONFIRMCHARGEBACK']).replace("#1", formattedAmount).replace("#2", this.transactionDetailData.id),
      header: this.i18n.i18nLng()['CONFIRMATION'],
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-question-circle',
      acceptButtonProps: {
        label: this.i18n.i18nLng()['CREDITBUTTON']
      },
      rejectButtonProps: {
        label: this.i18n.i18nLng()['CANCEL'],
        outlined: true,
        severity: 'secondary'
      },
      accept: () => {
        this.layoutService.progressDialogTitle = this.i18n.i18nLng()['PROCESSINGTRANSACTION'];
        this.layoutService.showProgressSpinner = true;
        this.layoutService.showProgressDialog = true;

        const transactionCreate: TransactionCreate = this.transactionCreateMapper.map(this.transactionDetailData, 'CREDIT');

        this.transactionHandlingService.createChildTransaction(SERVICE_ENDPOINTS.CREATE_TRANSACTION, 'credit', this.transactionDetailData.id, transactionCreate).subscribe({
          next: result => {
            this.layoutService.showProgressDialog = false;
            if (result.successful) {
              this.creditSuccessful.emit();
            } else {
              this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + result.message);
              this.showTransactionError.set(true);
            }
          },
          error: err => {
            this.layoutService.showProgressDialog = false;
            this.transactionErrorMessage.set(this.i18n.i18nLng()['PROCESSTRANSACTIONERROR'] + ': ' + err.message);
            this.showTransactionError.set(true);
          }
        })
      }
    });
  }

  /**
   * history transaction was selected
   * @param event
   * @protected
   */
  protected historyTransactionSelected(event: TableRowSelectEvent<DialogTableDataRow>) {
    this.transactionId = (event.data as DialogTableDataRow)?.id
    this.getTransactionDetails();
  }

  /**
   * trigger capture event
   * @protected
   */
  protected initCapture() {
    this.showTransactionError.set(false);
    this.transactionErrorMessage.set('');
    this.processCapture.emit(this.transactionDetailData);
  }

  /**
   * trigger void event
   * @protected
   */
  protected initVoid() {
    this.showTransactionError.set(false);
    this.transactionErrorMessage.set('');
    this.processVoid.emit(this.transactionDetailData);
  }

}
